import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import classNames from 'classnames';
import ReactSelect, { OnChangeValue } from 'react-select';

import { FormImageOption } from '../../../entities/@forms/Form/Form';
import { useTrans } from '../../../hooks';
import { IconChevronDown } from '../../Icon/set';
import { SelectLabel, SelectOption } from './subcomponents';

import './Select.scss';

interface SelectProps {
    isLoading: boolean;
    isLoadingMessage?: string;
    isSearchable?: boolean;
    name: string;
    value?: OnChangeValue<FormImageOption, false>;
    options: FormImageOption[];
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    onChange: (selected: FormImageOption) => void;
    className?: string;
}

export const Select: FC<SelectProps> = ({
    isLoading,
    isLoadingMessage,
    isSearchable,
    name,
    value,
    options,
    required,
    disabled,
    placeholder,
    onChange,
    className = '',
}): ReactElement | null => {
    const trans = useTrans();

    const [isMounted, setIsMounted] = useState<boolean>(false);

    useEffect((): void => {
        setIsMounted(true);
    }, []);

    const getLoadingMessage = (): string => isLoadingMessage || trans('components.select.loadingMessage');
    const getNoOptionsMessage = (): string => trans('components.select.nullState');

    const handleChange = (selectValue: OnChangeValue<FormImageOption, false>): void => {
        onChange(selectValue as FormImageOption);
    };

    const selectClassNames = classNames('select', {
        'select--is-disabled': disabled,
    }, className);

    // Unfortunate solution to SSR warning in console. Component may only render on client.
    if (!isMounted) {
        return null;
    }

    return (
        <ReactSelect
            isLoading={isLoading}
            loadingMessage={getLoadingMessage}
            isSearchable={isSearchable}
            name={name}
            value={value}
            options={options}
            required={required}
            isDisabled={disabled}
            placeholder={placeholder}
            noOptionsMessage={getNoOptionsMessage}
            components={{
                DropdownIndicator: IconChevronDown,
                Option: SelectOption,
                SingleValue: SelectLabel,
            }}
            onChange={handleChange}
            classNamePrefix="select"
            className={selectClassNames}
        />
    );
};
